import React from 'react';

import { useApplications } from 'hooks/api/useApplications';
import { useOrganizations } from 'hooks/api/useOrganizations';
import { useHasApplicationAccess } from 'hooks/useHasAccess';

/**
 * Get a user's applications grouped by organization.
 * Returns an array with organization ID, name, and a list of applications.
 * Only includes applications where the user has a role other than "user".
 */
export function useGroupedApplications() {
  const { data: organizations } = useOrganizations();
  const { data: applications } = useApplications();
  const hasApplicationAccess = useHasApplicationAccess();

  return React.useMemo(() => {
    if (!organizations || !applications) {
      return [];
    }

    return organizations
      .map((organization) => ({
        id: organization.id,
        name: organization.name,
        applications: applications
          .filter(
            (application) =>
              application.organization_id === organization.id &&
              hasApplicationAccess(application.id, organization.id)
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter((group) => group.applications.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations, applications, hasApplicationAccess]);
}
